import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import StepProgressIndicator from '../../components/ui/StepProgressIndicator';
import WorkflowBreadcrumb from '../../components/ui/WorkflowBreadcrumb';
import NavigationActions from '../../components/ui/NavigationActions';
import RadiusSelector from './components/RadiusSelector';
import VenueTypeFilters from './components/VenueTypeFilters';
import AdvancedFilters from './components/AdvancedFilters';
import FilteredResultsList from './components/FilteredResultsList';
import ResultsCounter from './components/ResultsCounter';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { trackFilterUsed } from '../../utils/analytics';


const LocationTypeFilter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const meetingIdFromState = location?.state?.meetingId;
  const sessionCodeFromState = location?.state?.sessionCode;
  const [radius, setRadius] = useState(5);
  const [selectedTypes, setSelectedTypes] = useState(['cafe', 'restaurant']);
  const [advancedFilters, setAdvancedFilters] = useState({
    accessible: false,
    parking: false,
    wifi: false,
    petFriendly: false,
    outdoor: false,
    openNow: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const [searchTimeoutId, setSearchTimeoutId] = useState(null);

  const mockVenues = [
    {
      id: 1,
      name: "The Daily Grind Coffee House",
      type: "Cafe",
      typeIcon: "Coffee",
      typeColor: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
      address: "342 Market Street, Downtown District",
      rating: 4.7,
      reviewCount: 284,
      participants: [
        { name: "Sarah Johnson", travelTime: "18 min", method: "driving" },
        { name: "Michael Chen", travelTime: "17 min", method: "public transit" },
        { name: "Emily Rodriguez", travelTime: "19 min", method: "bike" }
      ],
      features: ["WiFi", "Parking", "Outdoor Seating"],
      accessible: true,
      parking: true,
      wifi: true,
      petFriendly: false,
      outdoor: true,
      openNow: true
    },
    {
      id: 2,
      name: "Bella Vista Italian Kitchen",
      type: "Restaurant",
      typeIcon: "UtensilsCrossed",
      typeColor: "bg-orange-500/10 text-orange-700 dark:text-orange-400",
      address: "789 Riverside Avenue, Waterfront Area",
      rating: 4.5,
      reviewCount: 512,
      participants: [
        { name: "Sarah Johnson", travelTime: "16 min", method: "driving" },
        { name: "Michael Chen", travelTime: "18 min", method: "public transit" },
        { name: "Emily Rodriguez", travelTime: "17 min", method: "bike" }
      ],
      features: ["Wheelchair Accessible", "Parking", "Outdoor Seating"],
      accessible: true,
      parking: true,
      wifi: false,
      petFriendly: false,
      outdoor: true,
      openNow: true
    },
    {
      id: 3,
      name: "Central Library Reading Room",
      type: "Library",
      typeIcon: "BookOpen",
      typeColor: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
      address: "156 Education Boulevard, University District",
      rating: 4.8,
      reviewCount: 167,
      participants: [
        { name: "Sarah Johnson", travelTime: "20 min", method: "driving" },
        { name: "Michael Chen", travelTime: "19 min", method: "public transit" },
        { name: "Emily Rodriguez", travelTime: "21 min", method: "bike" }
      ],
      features: ["WiFi", "Wheelchair Accessible"],
      accessible: true,
      parking: false,
      wifi: true,
      petFriendly: false,
      outdoor: false,
      openNow: true
    },
    {
      id: 4,
      name: "Greenwood Community Park",
      type: "Park",
      typeIcon: "Trees",
      typeColor: "bg-green-500/10 text-green-700 dark:text-green-400",
      address: "2341 Park Lane, Greenwood Neighborhood",
      rating: 4.6,
      reviewCount: 423,
      participants: [
        { name: "Sarah Johnson", travelTime: "15 min", method: "driving" },
        { name: "Michael Chen", travelTime: "16 min", method: "public transit" },
        { name: "Emily Rodriguez", travelTime: "14 min", method: "bike" }
      ],
      features: ["Pet Friendly", "Parking"],
      accessible: true,
      parking: true,
      wifi: false,
      petFriendly: true,
      outdoor: true,
      openNow: true
    },
    {
      id: 5,
      name: "Riverside Dog Park & Recreation",
      type: "Dog Park",
      typeIcon: "Dog",
      typeColor: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
      address: "567 Riverside Drive, Waterfront District",
      rating: 4.9,
      reviewCount: 891,
      participants: [
        { name: "Sarah Johnson", travelTime: "22 min", method: "driving" },
        { name: "Michael Chen", travelTime: "21 min", method: "public transit" },
        { name: "Emily Rodriguez", travelTime: "23 min", method: "bike" }
      ],
      features: ["Pet Friendly", "Parking", "Outdoor Seating"],
      accessible: false,
      parking: true,
      wifi: false,
      petFriendly: true,
      outdoor: true,
      openNow: true
    },
    {
      id: 6,
      name: "FitZone Wellness Center",
      type: "Gym",
      typeIcon: "Dumbbell",
      typeColor: "bg-red-500/10 text-red-700 dark:text-red-400",
      address: "890 Fitness Boulevard, Sports Complex",
      rating: 4.4,
      reviewCount: 356,
      participants: [
        { name: "Sarah Johnson", travelTime: "19 min", method: "driving" },
        { name: "Michael Chen", travelTime: "20 min", method: "public transit" },
        { name: "Emily Rodriguez", travelTime: "18 min", method: "bike" }
      ],
      features: ["Parking", "WiFi"],
      accessible: true,
      parking: true,
      wifi: true,
      petFriendly: false,
      outdoor: false,
      openNow: false
    },
    {
      id: 7,
      name: "Marketplace Shopping Plaza",
      type: "Shopping",
      typeIcon: "ShoppingBag",
      typeColor: "bg-purple-500/10 text-purple-700 dark:text-purple-400",
      address: "1234 Commerce Street, Shopping District",
      rating: 4.3,
      reviewCount: 678,
      participants: [
        { name: "Sarah Johnson", travelTime: "17 min", method: "driving" },
        { name: "Michael Chen", travelTime: "18 min", method: "public transit" },
        { name: "Emily Rodriguez", travelTime: "16 min", method: "bike" }
      ],
      features: ["Wheelchair Accessible", "Parking", "WiFi"],
      accessible: true,
      parking: true,
      wifi: true,
      petFriendly: false,
      outdoor: false,
      openNow: true
    },
    {
      id: 8,
      name: "The Copper Mug Tavern",
      type: "Bar",
      typeIcon: "Wine",
      typeColor: "bg-pink-500/10 text-pink-700 dark:text-pink-400",
      address: "456 Nightlife Avenue, Entertainment District",
      rating: 4.6,
      reviewCount: 445,
      participants: [
        { name: "Sarah Johnson", travelTime: "21 min", method: "driving" },
        { name: "Michael Chen", travelTime: "22 min", method: "public transit" },
        { name: "Emily Rodriguez", travelTime: "20 min", method: "bike" }
      ],
      features: ["Outdoor Seating", "WiFi"],
      accessible: false,
      parking: false,
      wifi: true,
      petFriendly: false,
      outdoor: true,
      openNow: false
    }
  ];

  const totalVenues = mockVenues?.length;

  useEffect(() => {
    applyFilters();
  }, [radius, selectedTypes, advancedFilters]);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutId) {
        clearTimeout(searchTimeoutId);
      }
    };
  }, [searchTimeoutId]);

  const applyFilters = () => {
    // Clear any existing timeout
    if (searchTimeoutId) {
      clearTimeout(searchTimeoutId);
    }

    setIsLoading(true);
    setSearchError(null);
    
    // Set 10-second timeout
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      setSearchError('Search timed out after 10 seconds. Please try again.');
    }, 10000);
    
    setSearchTimeoutId(timeoutId);

    setTimeout(() => {
      // Clear timeout if search completes successfully
      clearTimeout(timeoutId);

      let filtered = mockVenues?.filter(venue => {
        const typeMatch = selectedTypes?.length === 0 || selectedTypes?.some(type => 
          venue?.type?.toLowerCase()?.replace(' ', '_') === type
        );

        const radiusMatch = true;

        const advancedMatch = 
          (!advancedFilters?.accessible || venue?.accessible) &&
          (!advancedFilters?.parking || venue?.parking) &&
          (!advancedFilters?.wifi || venue?.wifi) &&
          (!advancedFilters?.petFriendly || venue?.petFriendly) &&
          (!advancedFilters?.outdoor || venue?.outdoor) &&
          (!advancedFilters?.openNow || venue?.openNow);

        return typeMatch && radiusMatch && advancedMatch;
      });

      setFilteredResults(filtered);
      setIsLoading(false);
    }, 800);
  };

  const handleRetrySearch = () => {
    setSearchError(null);
    applyFilters();
  };

  const handleTypeToggle = (typeId) => {
    setSelectedTypes(prev => 
      prev?.includes(typeId) 
        ? prev?.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const handleVenueTypeToggle = (type) => {
    setSelectedTypes(prev => {
      const newTypes = prev?.includes(type)
        ? prev?.filter(t => t !== type)
        : [...prev, type];
      
      // Track filter usage
      trackFilterUsed('venue_type', type);
      
      return newTypes;
    });
  };

  const handleViewDetails = (venue) => {
    navigate(`/final-meeting-details?meetingId=${meetingIdFromState}`, { 
      state: { 
        selectedVenue: venue,
        meetingId: meetingIdFromState,
        sessionCode: sessionCodeFromState
      } 
    });
  };

  const handleClearFilters = () => {
    setRadius(5);
    setSelectedTypes([]);
    setAdvancedFilters({
      accessible: false,
      parking: false,
      wifi: false,
      petFriendly: false,
      outdoor: false,
      openNow: false
    });
  };

  const handleRadiusChange = (newRadius) => {
    setRadius(newRadius);
    
    // Track radius filter change
    trackFilterUsed('radius', `${newRadius}km`);
  };

  const hasActiveFilters = selectedTypes?.length > 0 || Object.values(advancedFilters)?.some(v => v);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <StepProgressIndicator />
      <WorkflowBreadcrumb />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 lg:py-12 pb-32">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Refine Your Search
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Filter meeting locations by venue type and preferences
              </p>
            </div>
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={handleClearFilters}
                iconName="X"
                iconPosition="left"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-6 md:space-y-8">
          {searchError ? (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertCircle" size={32} className="text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Search Timeout</h3>
              <p className="text-muted-foreground mb-4">{searchError}</p>
              <Button
                onClick={handleRetrySearch}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Retry Search
              </Button>
            </div>
          ) : (
            <>
              <ResultsCounter
                totalResults={totalVenues}
                filteredResults={filteredResults?.length}
                isLoading={isLoading}
              />

              <RadiusSelector
                radius={radius}
                onRadiusChange={handleRadiusChange}
              />

              <VenueTypeFilters
                selectedTypes={selectedTypes}
                onTypeToggle={handleVenueTypeToggle}
              />

              <AdvancedFilters
                filters={advancedFilters}
                onFilterChange={setAdvancedFilters}
              />

              <FilteredResultsList
                results={filteredResults}
                onViewDetails={handleViewDetails}
              />
            </>
          )}
        </div>
      </main>
      <NavigationActions
        isNextDisabled={filteredResults?.length === 0}
        nextLabel="Continue to Details"
        onNext={() => navigate('/final-meeting-details', {
          state: {
            meetingId: meetingIdFromState,
            sessionCode: sessionCodeFromState
          }
        })}
        onPrevious={() => navigate(-1)}
      />
    </div>
  );
};

export default LocationTypeFilter;